import GlobalSmsRateLimitQuery from '@/services/query/globalSmsRateLimit/GlobalSmsRateLimitQuery.services';
import GlobalSmsRateLimitCommand from '@/services/command/globalSmsRateLimit/GlobalSmsRateLimitCommand.services';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
    Box,
    Table,
    TableCell,
    TableRow,
    TableHead,
    Paper,
    TableContainer,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { SCUILoadingLinear } from '@/atomic/atoms/SCUILoadingLinear/SCUILoadingLinear';
import { MSM } from '../../membershipSmsManagement.classes';

export const MembershipSmsManagementTab = ({
    selectedCountry,
    selectedPlatform,
    platformList,
}: {
    selectedCountry: any;
    selectedPlatform: number;
    platformList: any;
}) => {
    const [countryLimitData, setCountryLimitData] = useState<any>([]);
    const [rateLimitSummary, setRateLimitSummary] = useState<any>([]);
    const [expanded, setExpanded] = useState<string | false>(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedLimits, setEditedLimits] = useState<{ [key: string]: number }>({});

    const { t } = useTranslation();

    useEffect(() => {
        if (selectedCountry?.countryId && selectedPlatform) {
            getCountryLimitByPlatform();
        }
    }, [selectedCountry?.countryId, selectedPlatform]);

    useEffect(() => {
        if (expanded) {
            getRateLimitSummary();
        }
    }, [expanded]);

    const getCountryLimitByPlatform = () => {
        setIsLoading(true);
        GlobalSmsRateLimitQuery.GetCountryLimitByPlatform({
            CountryCode: selectedCountry?.countryCode,
            Platform: selectedPlatform,
        })
            .then((res: any) => {
                if (res?.data) {
                    setCountryLimitData(Array.isArray(res.data) ? res.data : [res.data]);
                }
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const getRateLimitSummary = () => {
        if (!expanded) return;
        const index = parseInt(expanded.replace('panel-', ''));
        const endpoint = (countryLimitData || [])[index]?.endpoint;

        if (!endpoint) return;
        setIsLoading(true);

        // Get today's date in YYYY-MM-DD format
        const today = new Date().toISOString().split('T')[0];

        GlobalSmsRateLimitQuery.GetRateLimitSummary({
            CountryCode: selectedCountry?.countryCode,
            Platform: selectedPlatform,
            StartDate: today,
        })
            .then((res: any) => {
                if (res?.data) {
                    const data = Array.isArray(res.data) ? res.data : [res.data];
                    setRateLimitSummary(data);
                }
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleEditClick = (event: React.MouseEvent) => {
        event.stopPropagation();

        if (!isEditMode) {
            // Initialize editedLimits with current values when entering edit mode
            const expandedIndex = expanded ? parseInt(expanded.replace('panel-', '')) : 0;
            const currentItem = (countryLimitData || [])[expandedIndex];

            const initialLimits: { [key: string]: number } = {};
            Array.from({ length: 24 }).forEach((_, idx) => {
                const hourString = String(idx).padStart(2, '0');
                const limitData = currentItem?.values?.find((v: any) => parseInt(v.hour) === idx);
                initialLimits[hourString] = limitData?.limit || 0;
            });
            setEditedLimits(initialLimits);
            setIsEditMode(true);
        }
    };

    const handleSaveClick = (event: React.MouseEvent) => {
        event.stopPropagation();

        // Prepare data for API call
        const expandedIndex = expanded ? parseInt(expanded.replace('panel-', '')) : 0;
        const currentItem = (countryLimitData || [])[expandedIndex];

        // Convert editedLimits object to values array format
        const valuesArray = Object.entries(editedLimits).map(([hour, limit]) => ({
            hour: parseInt(hour),
            limit: Number(limit),
        }));

        const payload = {
            endpoint: currentItem?.endpoint,
            endpointLocalizeKey: currentItem?.endpointLocalizeKey,
            platform: selectedPlatform,
            countryCode: selectedCountry?.countryCode,
            values: valuesArray,
            userAgent: navigator.userAgent,
        };

        setIsLoading(true);

        GlobalSmsRateLimitCommand.InsertRateLimit(payload)
            .then(() => {
                Swal.fire(t('general.success'), '', 'success');
                setIsEditMode(false);
                setEditedLimits({});
                // Refresh data
                getCountryLimitByPlatform();
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleLimitChange = (hour: string, value: string) => {
        setEditedLimits((prev) => ({
            ...prev,
            [hour]: parseInt(value) || 0,
        }));
    };

    const handleCancelClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        setIsEditMode(false);
        setEditedLimits({});
    };

    return (
        <Box className={MSM.membershipTabRoot} sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <SCUILoadingLinear display={isLoading} />
            <TableContainer className={MSM.membershipTabTableWrap} sx={{ width: '100%', boxShadow: 3 }} component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '14px', width: '20%', p: 2 }}>
                                {t('membershipSmsManagement.country')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '14px', width: '40%', p: 2 }}>
                                {t('membershipSmsManagement.endpoint')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '14px', width: '40%', p: 2 }}>
                                {t('membershipSmsManagement.platform')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '14px', width: '48px', p: 2 }}></TableCell>
                        </TableRow>
                    </TableHead>
                </Table>
                <Box>
                    {(countryLimitData || []).map((item: any, index: any) => (
                        <Accordion
                            className={MSM.membershipTabAccordion}
                            key={item.endpoint}
                            expanded={expanded === `panel-${index}`}
                            onChange={handleAccordionChange(`panel-${index}`)}
                            disableGutters
                            elevation={0}
                            sx={{
                                '&:before': {
                                    display: 'none',
                                },
                                borderTop: index === 0 ? 'none' : '1px solid rgba(224, 224, 224, 1)',
                                '&:not(:last-child)': {
                                    borderBottom: 0,
                                },
                                '&.Mui-expanded': {
                                    margin: 0,
                                },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`panel-${index}-content`}
                                id={`panel-${index}`}
                                sx={{
                                    minHeight: '48px',
                                    '&.Mui-expanded': {
                                        minHeight: '48px',
                                    },
                                    '& .MuiAccordionSummary-content': {
                                        margin: 0,
                                        '&.Mui-expanded': {
                                            margin: 0,
                                        },
                                    },
                                    px: 2,
                                    py: 1.5,
                                }}
                            >
                                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                                    <Box sx={{ width: '20%' }}>
                                        <Typography sx={{ fontSize: '14px' }}>
                                            {selectedCountry?.translatedCountryName} | {selectedCountry?.countryId}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ width: '40%' }}>
                                        <Typography sx={{ fontSize: '14px' }}>{item.endpoint}</Typography>
                                    </Box>
                                    <Box sx={{ width: '40%' }}>
                                        <Typography sx={{ fontSize: '14px', paddingLeft: '8px' }}>
                                            {
                                                (platformList || [])?.find(
                                                    (platform: any) => platform.id === selectedPlatform
                                                )?.name
                                            }
                                        </Typography>
                                    </Box>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails
                                className={MSM.membershipTabAccordionDetails}
                                sx={{
                                    p: 2,
                                    border: '1px solid #e0e0e0',
                                    borderTop: '1px solid rgba(224, 224, 224, 1)',
                                    backgroundColor: '#f7f7f7',
                                }}
                            >
                                <Box>
                                    <Box
                                        className={MSM.membershipTabRateToolbar}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mb: 3,
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
                                            {t('membershipSmsManagement.rateLimitDaily')}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            {isEditMode && (
                                                <IconButton
                                                    className={MSM.inputButtonCancelEdit}
                                                    onClick={(e) => handleCancelClick(e)}
                                                    sx={{
                                                        width: '96px',
                                                        height: '36px',
                                                        borderRadius: '8px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <CancelIcon sx={{ fontSize: '20px', marginRight: '4px' }} />
                                                    <Typography sx={{ fontSize: '14px' }}>
                                                        {t('general.cancel')}
                                                    </Typography>
                                                </IconButton>
                                            )}
                                            <IconButton
                                                className={MSM.inputButtonSaveOrEdit}
                                                onClick={(e) => (isEditMode ? handleSaveClick(e) : handleEditClick(e))}
                                                sx={{
                                                    width: '96px',
                                                    height: '36px',
                                                    borderRadius: '8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {isEditMode ? (
                                                    <>
                                                        <SaveIcon sx={{ fontSize: '20px', marginRight: '4px' }} />
                                                        <Typography sx={{ fontSize: '14px' }}>
                                                            {t('general.save')}
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    <>
                                                        <EditIcon sx={{ fontSize: '20px', marginRight: '4px' }} />
                                                        <Typography sx={{ fontSize: '14px' }}>
                                                            {t('general.edit')}
                                                        </Typography>
                                                    </>
                                                )}
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    <Box
                                        className={MSM.membershipTabHourGrid}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(6, 1fr)',
                                            gap: '16px',
                                            width: '100%',
                                        }}
                                    >
                                        {Array.from({ length: 24 }).map((_, idx) => {
                                            // Get the current item's rate limit values from countryLimitData
                                            const expandedIndex = expanded
                                                ? parseInt(expanded.replace('panel-', ''))
                                                : 0;
                                            const currentItem = (countryLimitData || [])[expandedIndex];

                                            // Find the limit for this hour from the values array
                                            const hourString = String(idx).padStart(2, '0');
                                            const limitData = currentItem?.values?.find(
                                                (v: any) => parseInt(v.hour) === idx
                                            );
                                            const limit = limitData?.limit || 0;

                                            // Find rate limit summary data for this hour using pre-calculated hour field
                                            const summaryData = (rateLimitSummary || []).find(
                                                (log: any) => parseInt(log.hour) === idx
                                            );
                                            const requestCount = summaryData?.requestCount || 0;

                                            // Determine color based on request count vs limit
                                            let barColor = '#000000';
                                            if (requestCount === 0) {
                                                barColor = '#d1d5db'; // Gray for no requests
                                            } else if (limit === 0) {
                                                barColor = '#000000'; // Black if no limit set
                                            } else {
                                                const percentage = (requestCount / limit) * 100;
                                                if (percentage >= 90) {
                                                    barColor = '#ef4444'; // Red when 90% or more of limit
                                                } else if (percentage >= 50) {
                                                    barColor = '#eab308'; // Yellow when 50% to 90% of limit
                                                } else {
                                                    barColor = '#000000'; // Black when less than 50%
                                                }
                                            }

                                            // Calculate bar width percentage based on limit (0-100%)
                                            const widthPercent =
                                                limit > 0 ? Math.min((requestCount / limit) * 100, 100) : 0;

                                            return (
                                                <Box
                                                    key={idx}
                                                    className={MSM.membershipTabHourCell}
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'flex-start',
                                                    }}
                                                >
                                                    {/* Time label */}
                                                    <Typography
                                                        sx={{
                                                            fontSize: '11px',
                                                            color: '#666',
                                                            mb: 0.5,
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {`${String(idx).padStart(2, '0')}:00`}
                                                    </Typography>

                                                    {isEditMode ? (
                                                        // Edit Mode: Show TextField
                                                        <TextField
                                                            className={MSM.inputTextHourLimit}
                                                            type="number"
                                                            size="small"
                                                            value={editedLimits[hourString] ?? limit}
                                                            onChange={(e) =>
                                                                handleLimitChange(hourString, e.target.value)
                                                            }
                                                            sx={{
                                                                width: '100%',
                                                                '& .MuiInputBase-input': {
                                                                    fontSize: '12px',
                                                                    padding: '4px 8px',
                                                                    textAlign: 'center',
                                                                },
                                                            }}
                                                            slotProps={{
                                                                htmlInput: {
                                                                    min: 0,
                                                                    step: 1,
                                                                },
                                                            }}
                                                        />
                                                    ) : (
                                                        <>
                                                            {/* View Mode: Show Progress Bar */}
                                                            {/* Progress bar container */}
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    height: '8px',
                                                                    backgroundColor: '#e0e0e0',
                                                                    borderRadius: '4px',
                                                                    overflow: 'hidden',
                                                                    mb: 0.5,
                                                                }}
                                                            >
                                                                {/* Progress bar fill */}
                                                                <Box
                                                                    sx={{
                                                                        width: `${widthPercent}%`,
                                                                        height: '100%',
                                                                        backgroundColor: barColor,
                                                                        transition: 'width 0.5s ease',
                                                                    }}
                                                                />
                                                            </Box>

                                                            {/* Count label */}
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '10px',
                                                                    color: '#666',
                                                                    fontWeight: 400,
                                                                }}
                                                            >
                                                                {requestCount}/{limit}
                                                            </Typography>
                                                        </>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </TableContainer>
        </Box>
    );
};
